import React, { useEffect } from 'react';
import { Modal, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import moment from 'moment';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';

import CustomButton from '../../../components/CustomButton';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { CREATE_REWARD_V2 } from '../../../apollo/reward/Mutation';
import { GET_REWARDS_V2} from '../../../apollo/reward/Query';
import { GET_PRODUCTS } from '../../../apollo/product/query';
import { GET_CATEGORYS } from '../../../apollo/category/query';
import { Autocomplete, TextField } from '@mui/material';

const ImageThumb = ({ image }) => {
  return (
    <img
      src={URL.createObjectURL(image)}
      alt={image.name}
      style={{
        objectFit: "contain",
        borderRadius: 8,
        height: 200,
        width: 200,
      }}
    />
  );
};

function ModalAddReward({
  show,
  onClose,
  editingReward,
  onSubmit,
  file,
  onUpload,
  userTypeLoading,
  userTypeData,
}) {
  const [createRewardV2, { loading: creating, error: createError }] = useMutation(CREATE_REWARD_V2);
  const shopId = JSON.parse(localStorage.getItem('SHOP_DATA'))?.id || JSON.parse(localStorage.getItem('USER_DATA'))?.data?.shop?.id;
  
  const [loadProducts, { data: productData, loading: productLoading }] = useLazyQuery(GET_PRODUCTS);
  const [loadCategories, { data: categoryData, loading: categoryLoading }] = useLazyQuery(GET_CATEGORYS);

  useEffect(() => {
    if (show) {
      loadProducts({ variables: { limit: 100, where: { shop: shopId, isDeleted: false } } });
      loadCategories({ variables: { limit: 100, where: { shop: shopId, isDeleted: false } } });
    }
  }, [show, shopId, loadProducts, loadCategories]);

  const onSearchProducts = (value) => {
    loadProducts({ 
      variables: { 
        limit: 100, 
        where: { 
          shop: shopId, 
          name: value,
          isDeleted: false
        } 
      } 
    });
  };

  const onSearchCategories = (value) => {
    loadCategories({ 
      variables: { 
        limit: 100, 
        where: { 
          shop: shopId, 
          name: value,
          isDeleted: false
        } 
      } 
    });
  };

  const submitReward = async (values, { setSubmitting }) => {
    try {
      const normalizeRewardStatus = (status) => {
        if (status === true) return "USABLE";
        if (status === false) return "DISABLE";
        if (status === "EXPIRE") return "EXPIRED";
        if (!status) return "USABLE";
        return status;
      };

      // Combine date + time to ISO string
      const combineDT = (dateStr, timeStr, defaultTime) => {
        if (!dateStr && !timeStr) return null;
        const time = timeStr || defaultTime;
        const d = new Date(`${dateStr}T${time}`);
        return isNaN(d) ? null : d.toISOString();
      };

      const normalizedStatus = normalizeRewardStatus(values.status);
      const payload = {
        name: values.name,
        code: values.code || null,
        description: values.description || '',
        image: null,
        percent: values.percent !== '' && values.percent != null ? Number(values.percent) : null,
        pointMax: values.pointMax !== '' && values.pointMax != null ? Number(values.pointMax) : null,
        pointMin: values.pointMin !== '' && values.pointMin != null ? Number(values.pointMin) : null,
        maxDiscountAmount: values.maxDiscountAmount !== '' && values.maxDiscountAmount != null ? Number(values.maxDiscountAmount) : null,
        maxUser: values.maxUser !== '' && values.maxUser != null ? Number(values.maxUser) : null,
        remainUser: values.remainUser !== '' && values.remainUser != null ? Number(values.remainUser) : null,
        minimumOrderAmount: values.minimumOrderAmount !== '' && values.minimumOrderAmount != null ? Number(values.minimumOrderAmount) : null,
        minimumOrderQuantity: values.minimumOrderQuantity !== '' && values.minimumOrderQuantity != null ? Number(values.minimumOrderQuantity) : null,
        freeItemQuantity: values.freeItemQuantity !== '' && values.freeItemQuantity != null ? Number(values.freeItemQuantity) : null,
        rewardType: values.rewardType,
        applyType: values.applyType,
        applicableProducts: values.applyType === 'SPECIFIC_PRODUCTS' ? values.applicableProducts : [],
        applicableCategories: values.applyType === 'SPECIFIC_CATEGORIES' ? values.applicableCategories : [],
        userType: values.userType,
        startDate: combineDT(
          values.startDate,
          values.startHour && values.startMinute ? `${values.startHour}:${values.startMinute}` : null,
          "00:00"
        ),
        endDate: combineDT(
          values.endDate,
          values.endHour && values.endMinute ? `${values.endHour}:${values.endMinute}` : null,
          "23:59"
        ),
        status: normalizedStatus,
      };
      await createRewardV2({
        variables: { data: payload },
        optimisticResponse: {
          createRewardV2: {
            __typename: 'RewardV2',
            id: `temp-${Date.now()}`,
            name: payload.name,
            code: payload.code,
            description: payload.description,
            image: payload.image,
            maxDiscountAmount: payload.maxDiscountAmount,
            percent: payload.percent,
            pointMax: payload.pointMax,
            pointMin: payload.pointMin,
            remainUser: payload.remainUser,
            rewardType: payload.rewardType,
            applyType: payload.applyType,
            applicableProducts: payload.applicableProducts,
            applicableCategories: payload.applicableCategories,
            minimumOrderAmount: payload.minimumOrderAmount,
            minimumOrderQuantity: payload.minimumOrderQuantity,
            freeItemQuantity: payload.freeItemQuantity,
            startDate: payload.startDate,
            endDate: payload.endDate,
            status: normalizedStatus,
          },
        },
        update: (cache, { data }) => {
          const newReward = data?.createRewardV2;
          try {
            const existing = cache.readQuery({ query: GET_REWARDS_V2 });
            if (existing?.getRewardsV2?.data) {
              cache.writeQuery({
                query: GET_REWARDS_V2,
                data: {
                  getRewardsV2: {
                    ...existing.getRewardsV2,
                    total: (existing.getRewardsV2.total ?? 0) + 1,
                    data: [newReward, ...existing.getRewardsV2.data],
                  },
                },
              });
            }
          } catch (err) {
            // cache may not be populated yet
          }
        },
        refetchQueries: [{ query: GET_REWARDS_V2 }],
        awaitRefetchQueries: true,
      });
      setSubmitting(false);
      if (typeof onSubmit === 'function') {
        onSubmit(values);
      }
      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (e) {
      setSubmitting(false);
      console.error('CREATE_REWARD_V2 error', e);
    }
  };
  
  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          ເພີ່ມລາງວັນ
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={{
            name: editingReward?.name || '',
            code: editingReward?.code || '',
            pointMin: editingReward?.pointMin || '',
            pointMax: editingReward?.pointMax || '',
            percent: editingReward?.percent || '',
            maxDiscountAmount: editingReward?.maxDiscountAmount || '',
            maxUser: editingReward?.maxUser || '',
            remainUser: editingReward?.remainUser || '',
            minimumOrderAmount: editingReward?.minimumOrderAmount || '',
            minimumOrderQuantity: editingReward?.minimumOrderQuantity || '',
            freeItemQuantity: editingReward?.freeItemQuantity || '',
            rewardType: editingReward?.rewardType || 'DISCOUNT',
            applyType: editingReward?.applyType || 'ALL',
            autoGenerateCode: false,
            applicableProducts: editingReward?.applicableProducts?.map(i => i.id || i) || [],
            applicableCategories: editingReward?.applicableCategories?.map(i => i.id || i) || [],
            startDate: editingReward?.startDate ? moment(editingReward.startDate).format('YYYY-MM-DD') : '',
            endDate: editingReward?.endDate ? moment(editingReward.endDate).format('YYYY-MM-DD') : '',
            // add 24h time selection (hour/minute)
            startHour: editingReward?.startDate ? moment(editingReward.startDate).format('HH') : '00',
            startMinute: editingReward?.startDate ? moment(editingReward.startDate).format('mm') : '00',
            endHour: editingReward?.endDate ? moment(editingReward.endDate).format('HH') : '23',
            endMinute: editingReward?.endDate ? moment(editingReward.endDate).format('mm') : '59',
            description: editingReward?.description || '',
            status:
              editingReward?.status === true
                ? "USABLE"
                : editingReward?.status === false
                  ? "DISABLE"
                  : editingReward?.status === "EXPIRE"
                    ? "EXPIRED"
                    : editingReward?.status || "USABLE",
            userType: editingReward?.userType || ''
          }}
          onSubmit={submitReward}
           enableReinitialize
        >
          {({ values, handleChange, handleSubmit, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຊື່ລາງວັນ <span style={{color: 'red'}}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      placeholder="ກະລຸນາປ້ອນຊື່ລາງວັນ"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Label>ລະຫັດລາງວັນ</Form.Label>
                      <Form.Check 
                        type="checkbox"
                        label="ສ້າງອໍໂຕ້"
                        name="autoGenerateCode"
                        checked={values.autoGenerateCode}
                        onChange={handleChange}
                        id="auto-gen-code"
                      />
                    </div>
                    <Form.Control
                      type="text"
                      name="code"
                      value={values.code}
                      onChange={handleChange}
                      placeholder={values.autoGenerateCode ? "ລະຫັດຈະຖືກສ້າງອໍໂຕ້" : "ກະລຸນາປ້ອນລະຫັດລາງວັນ"}
                      disabled={values.autoGenerateCode}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຄະແນນ <span style={{color: 'red'}}>*</span></Form.Label>
                    <Form.Control
                      type="number"
                      name="pointMin"
                      style={{ width: "100%" }}
                      value={values.pointMin}
                      onChange={handleChange}
                      placeholder="ກະລຸນາປ້ອນຄະແນນທີ່ຕ້ອງແລກ"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ປະເພດລາງວັນ <span style={{color: 'red'}}>*</span></Form.Label>
                    <Form.Select
                      name="rewardType"
                      value={values.rewardType}
                      onChange={handleChange}
                      required
                    >
                      <option value="DISCOUNT">ສ່ວນຫຼຸດ</option>
                      <option value="ITEM">ສິນຄ້າ</option>
                      <option value="FREE_SHIPPING">ສົ່ງຟຣີ</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຈຳນວນລາງວັນ</Form.Label>
                     <Form.Control
                       type="number"
                       name="maxUser"
                       style={{ width: "100%" }}
                       value={values.maxUser}
                       onChange={handleChange}
                       placeholder="ກະລຸນາປ້ອນຈຳນວນລາງວັນ"
                      //  min="1"
                     />
                   </Form.Group>
                 </Col>
                 <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຍອດຊື້ຂັ້ນຕ່ຳ (ກີບ)</Form.Label>
                     <Form.Control
                       type="number"
                       name="minimumOrderAmount"
                       style={{ width: "100%" }}
                       value={values.minimumOrderAmount}
                       onChange={handleChange}
                       placeholder="ກະລຸນາປ້ອນຍອດຊື້ຂັ້ນຕ່ຳ"
                       min="0"
                     />
                    </Form.Group>
                  </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຈຳນວນຊື້ຂັ້ນຕ່ຳ</Form.Label>
                     <Form.Control
                       type="number"
                       name="minimumOrderQuantity"
                       style={{ width: "100%" }}
                       value={values.minimumOrderQuantity}
                       onChange={handleChange}
                       placeholder="ກະລຸນາປ້ອນຈຳນວນຊື້ຂັ້ນຕ່ຳ"
                       min="0"
                     />
                   </Form.Group>
                 </Col>
                 <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຈຳນວນທີ່ແຖມ/ຟຣີ</Form.Label>
                     <Form.Control
                       type="number"
                       name="freeItemQuantity"
                       style={{ width: "100%" }}
                       value={values.freeItemQuantity}
                       onChange={handleChange}
                       placeholder="ກະລຸນາປ້ອນຈຳນວນທີ່ແຖມ"
                       min="0"
                     />
                    </Form.Group>
                  </Col>
              </Row>

              <Row>
                {/* <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຄະແນນສູງສຸດ </Form.Label>
                    <Form.Control
                      type="number"
                      name="pointMax"
                      style={{ width: "100%" }}
                      value={values.pointMax}
                      onChange={handleChange}
                      placeholder="ກະລຸນາປ້ອນຄະແນນສູງສຸດ"
                    />
                  </Form.Group>
                </Col> */}
                {
                  values.rewardType === 'DISCOUNT' && (
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>ເປີເຊັນສ່ວນຫຼຸດ (%)</Form.Label>
                        <Form.Control
                          type="number"
                          name="percent"
                          style={{ width: "100%" }}
                          value={values.percent}
                          onChange={handleChange}
                          placeholder="ກະລຸນາປ້ອນເປີເຊັນສ່ວນຫຼຸດ"
                          min="0"
                          max="100"
                        />
                      </Form.Group>
                    </Col>
                  )
                }
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ປະເພດລູກຄ້າ</Form.Label>
                    {userTypeLoading || !(userTypeData?.getUserTypes?.length > 0) ? (
                      <p>ບໍ່ພົບຂໍ້ມູນ ກະລຸນາເພີ່ມປະເພດລູກຄ້າກ່ອນ</p>
                    ) : (
                      <Form.Select
                        name="userType"
                        value={values.userType}
                        onChange={handleChange}
                        aria-label="Select user type"
                      >
                        <option value="">
                        {userTypeLoading 
                          ? 'ກຳລັງໂຫຼດຂໍ້ມູນ...' 
                          : 'ເລືອກປະເພດລູກຄ້າ...'}
                      </option>
                        <option value="all">ໃຊ້ໄດ້ທຸກປະເພດລູກຄ້າ</option>
                        {userTypeData?.getUserTypes
                          .filter((item) => item?.isOpen)
                          .map((userType) => (
                            <option key={userType?.id} value={userType?.id}>
                              {userType?.name}
                            </option>
                          ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ປະເພດການນຳໃຊ້</Form.Label>
                    <Form.Select
                      name="applyType"
                      value={values.applyType}
                      onChange={handleChange}
                    >
                      <option value="ALL">ທັງໝົດ</option>
                      <option value="SPECIFIC_PRODUCTS">ສິນຄ້າທີ່ກ່ຽວຂ້ອງ</option>
                      <option value="SPECIFIC_CATEGORIES">ໝວດໝູ່ທີ່ກ່ຽວຂ້ອງ</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {values.applyType === 'SPECIFIC_PRODUCTS' && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>ສິນຄ້າທີ່ກ່ຽວຂ້ອງ</Form.Label>
                      <Autocomplete
                        multiple
                        options={productData?.products?.data || []}
                        getOptionLabel={(option) => option.name || ""}
                        value={(productData?.products?.data || []).filter(p => values.applicableProducts.includes(p.id))}
                        onChange={(event, newValue) => {
                          setFieldValue('applicableProducts', newValue.map(item => item.id));
                        }}
                        loading={productLoading}
                        onInputChange={(event, newInputValue) => {
                          onSearchProducts(newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="ເລືອກສິນຄ້າ"
                            variant="outlined"
                            size="small"
                          />
                        )}
                        filterOptions={(x) => x}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {values.applyType === 'SPECIFIC_CATEGORIES' && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>ໝວດໝູ່ສິນຄ້າທີ່ກ່ຽວຂ້ອງ</Form.Label>
                      <Autocomplete
                        multiple
                        options={categoryData?.categories?.data || []}
                        getOptionLabel={(option) => option.name || ""}
                        value={(categoryData?.categories?.data || []).filter(c => values.applicableCategories.includes(c.id))}
                        onChange={(event, newValue) => {
                          setFieldValue('applicableCategories', newValue.map(item => item.id));
                        }}
                        loading={categoryLoading}
                        onInputChange={(event, newInputValue) => {
                          onSearchCategories(newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="ເລືອກໝວດໝູ່"
                            variant="outlined"
                            size="small"
                          />
                        )}
                        filterOptions={(x) => x}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Form.Group className="mb-3">
                <Form.Label>ລາຍລະອຽດ</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  placeholder="ກະລຸນາປ້ອນລາຍລະອຽດຂອງລາງວັນ"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ວັນທີເລີ່ມຕົ້ນ <span style={{color: 'red'}}>*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={values.startDate}
                      onChange={handleChange}
                      required
                    />
                    {/* time input for start (24-hour select) */}
                    <Form.Group className="mt-2">
                      <Form.Label>ເວລາເລີ່ມຕົ້ນ</Form.Label>
                      <Row>
                        <Col>
                          <Form.Select
                            name="startHour"
                            value={values.startHour}
                            onChange={handleChange}
                            aria-label="Hour"
                          >
                            {[...Array(24).keys()].map((h) => (
                              <option key={h} value={String(h).padStart(2, '0')}>
                                {String(h).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col>
                          <Form.Select
                            name="startMinute"
                            value={values.startMinute}
                            onChange={handleChange}
                            aria-label="Minute"
                          >
                            {[...Array(60).keys()].map((m) => (
                              <option key={m} value={String(m).padStart(2, '0')}>
                                {String(m).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    </Form.Group>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ວັນທີສິ້ນສຸດ <span style={{color: 'red'}}>*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={values.endDate}
                      onChange={handleChange}
                      required
                    />
                    {/* time input for end (24-hour select) */}
                    <Form.Group className="mt-2">
                      <Form.Label>ເວລາສິ້ນສຸດ</Form.Label>
                      <Row>
                        <Col>
                          <Form.Select
                            name="endHour"
                            value={values.endHour}
                            onChange={handleChange}
                            aria-label="Hour"
                          >
                            {[...Array(24).keys()].map((h) => (
                              <option key={h} value={String(h).padStart(2, '0')}>
                                {String(h).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col>
                          <Form.Select
                            name="endMinute"
                            value={values.endMinute}
                            onChange={handleChange}
                            aria-label="Minute"
                          >
                            {[...Array(60).keys()].map((m) => (
                              <option key={m} value={String(m).padStart(2, '0')}>
                                {String(m).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    </Form.Group>
                  </Form.Group>
                </Col>
              </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                    <Form.Label>ສະຖານະ</Form.Label>
                      <Form.Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                      >
                        <option value="USABLE">ໃຊ້ງານໄດ້</option>
                        <option value="EXPIRED">ໝົດອາຍຸ</option>
                        <option value="DISABLE">ປິດການໃຊ້ງານ</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                {values.rewardType !== 'ITEM' && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ຫຼຸດສູງສຸດ (ກີບ)</Form.Label>
                      <Form.Control
                        type="number"
                        name="maxDiscountAmount"
                        style={{ width: "100%" }}
                        value={values.maxDiscountAmount}
                        onChange={handleChange}
                        placeholder="ກະລຸນາປ້ອນຈຳນວນຫຼຸດສູງສຸດ"
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                )}
                </Row>

                {/* <Form.Group className="mb-3">
                <Form.Label>ຮູບພາບລາງວັນ</Form.Label>
                <div style={{ textAlign: "center" }}>
                  <input
                    type="file"
                    id="file-upload"
                    onChange={onUpload}
                    accept="image/*"
                    hidden
                  />
                  <label style={{ width: 200 }} htmlFor="file-upload">
                    <div
                      style={{
                        backgroundColor: "#F9FAFB",
                        height: "200px",
                        width: "100%",
                        border: "1px dashed #93989F",
                        borderRadius: 8,
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}>
                      {file ? (
                        <ImageThumb image={file} />
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                          }}>
                          <CloudUploadOutlinedIcon
                            sx={{ fontSize: 50, color: "#93989F" }}
                          />
                          <p style={{ color: "#93989F" }}>ເພີ່ມຮູບພາບລາງວັນ</p>
                          <p
                            style={{
                              color: "#3C169B",
                              textDecoration: "underline",
                            }}>
                            Browse
                          </p>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </Form.Group> */}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <Button
                  variant="secondary"
                  onClick={onClose}
                >
                  ຍົກເລີກ
                </Button>
                <CustomButton
                  type="submit"
                  disabled={creating}
                  title={creating ? <Spinner/> : 'ເພີ່ມ'}
                  width="130px"
                  borderRadius="8px" 
                />
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}

export default ModalAddReward;
